<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="isVisible => emit('update:isVisible', isVisible)" size="large" :isClosable="false" :isCloseButtonVisible="!isSaving">
    <template #content>
      <div
        :style="{
          opacity: isSaving ? '0.5' : '1',
          pointerEvents: isSaving ? 'none' : 'auto',
        }"
      >
        <div style="margin-bottom: 16px; margin-left: -5px">
          <SrpSegmentedControl v-model="mode" :options="modeOptions" size="md" aria-label="Post creation mode" />
        </div>

        <div style="padding-bottom: 12px; border-bottom: 1px solid #e0e0e0">
          <div class="create-type-row">
            <h2 class="create-type-row__label">Create a</h2>
            <SrpSegmentedControl v-model="postType" :options="postTypeSegmentOptions" size="md" aria-label="Post type" />
          </div>
          <p v-if="showAiPostOnlyWarning" class="create-type-row__ai-warning">AI assisted only supports Posts currently.</p>
        </div>

        <div style="min-height: 300px">
          <!-- AI Assisted Mode -->
          <div v-if="mode === 'ai'">
            <div style="margin-top: 20px">
              <label class="input-label">Theme/Purpose</label>
              <input
                :class="{
                  'global-text-input': true,
                  'global-text-input--error': !isFormValid && !_title,
                }"
                v-model="_title"
                style="max-width: 300px"
                placeholder="e.g. festival, local food..."
                type="text"
                @focus="isFormValid = true"
              />
            </div>

            <div style="margin-top: 16px">
              <label class="input-label">Guidance for the AI</label>
              <textarea
                :class="{
                  'global-textarea': true,
                  'global-textarea--error': !isFormValid && !_description,
                }"
                v-model="_description"
                style="height: 85px; min-height: 85px"
                placeholder="Describe the tone, style, key points to include..."
                @focus="isFormValid = true"
              ></textarea>
            </div>

            <div style="margin-top: 20px">
              <h4 style="margin-bottom: 8px">Content Album</h4>
              <div v-if="!selectedContentSummary">
                We'll find a content album to match with, or<br />
                <LinkWithIcon isDottedUnderline @click="isPickContentModalVisible = true" style="margin-top: 8px">
                  <template #icon>
                    <IconEmbedded name="folder_2" :size="19" />
                  </template>
                  <span>Manually choose one</span>
                </LinkWithIcon>
              </div>
              <div v-else style="margin-top: 8px">
                <ContentSummary
                  :contentSummaryData="selectedContentSummary"
                  :isContentSourcedFromLabelVisible="false"
                  :hideDecorations="true"
                  :customerId="props.customerId"
                  @clickViewPhotos="handleViewPhotos"
                />
              </div>
            </div>
          </div>

          <!-- Manual Mode -->
          <div v-if="mode === 'manual'">
            <div v-if="!isStoryPost" style="margin-top: 20px">
              <label class="input-label">Caption</label>
              <PostCaptionEditorPanel
                v-model:caption="_caption"
                :customerId="props.customerId"
                :assetIds="manualSelectedAssetIds"
                aiLinkColor="gray"
                :hasError="!isFormValid && isCaptionRequired && !_caption"
                placeholder="Write your post caption..."
                @focus="isFormValid = true"
              />
            </div>

            <div style="margin-top: 20px">
              <label class="input-label">{{ mediaSectionLabel }}</label>

              <div
                :class="{
                  'photo-section-container': true,
                  'photo-section-container--error': !isFormValid && manualSelectedAssetIds.length === 0,
                }"
                @click="isFormValid = true"
              >
                <PostMediaSelectionPanel
                  v-model:selectedAssetIds="manualSelectedAssetIds"
                  v-model:uploadedAssetIds="uploadedAssetIds"
                  :customerId="props.customerId"
                  :postType="postType"
                  :displayedAssetIds="displayedManualAlbumAssetIds"
                  inputId="new-post-manual-upload"
                  galleryPostfix="manualAlbumSelection"
                  :isLoadingAlbum="isLoadingManualAlbum"
                  :showThumbnailGrid="Boolean(manualAlbumPayload)"
                  :albumPickerLabel="manualAlbumPayload ? 'Change Album' : 'Select from an Album'"
                  :hasAlbum="Boolean(manualAlbumPayload)"
                  @openAlbumPicker="isManualAlbumPickerOpen = true"
                  @selectionChanged="handleMediaSelectionChanged"
                  @uploadSelected="handlePhotoUploadSelect"
                  @uploadDeselected="handlePhotoUploadDeselect"
                />
              </div>

              <div v-if="!isFormValid && manualValidationErrorMessage" class="global-error-string" style="margin-top: 8px">{{ manualValidationErrorMessage }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer="{ closeModal }">
      <div style="gap: 12px; display: flex">
        <SrpButton
          fill="outlined"
          color="gray"
          @click="
            () => {
              emit('cancel');
              closeModal();
            }
          "
          :isDisabled="isSaving"
        >
          Cancel
        </SrpButton>

        <SrpButton
          @click="
            async () => {
              const success = await save();
              if (success) {
                closeModal();
              }
            }
          "
          :isDisabled="isSaving"
        >
          <template #icon>
            <LoadingIcon v-if="isSaving" :size="20" />
            <IconEmbedded v-else :name="mode === 'manual' ? 'save_2-5' : 'ai_2'" :size="20" />
          </template>
          {{ buttonText }}
        </SrpButton>
      </div>
    </template>
  </SrpModal>

  <PickFromExistingContentModal
    v-if="isPickContentModalVisible"
    v-model:isVisible="isPickContentModalVisible"
    :customerId="props.customerId"
    :calendarItem="null"
    @close="isPickContentModalVisible = false"
    @picked="contentSummary => handleContentSummarySelected(contentSummary)"
  />

  <ViewAllPhotosModal v-if="isViewAllPhotosModalVisible" v-model:isVisible="isViewAllPhotosModalVisible" :customerId="props.customerId" :contentSummaryId="viewAllPhotosSummaryId" />

  <PickFromExistingContentModal
    v-if="isManualAlbumPickerOpen"
    v-model:isVisible="isManualAlbumPickerOpen"
    :customerId="props.customerId"
    :calendarItem="null"
    :videoOnly="videoOnlyForAlbumPicker"
    :showPhotoAndVideoCount="showAlbumPhotoAndVideoCount"
    @close="isManualAlbumPickerOpen = false"
    @picked="handleManualAlbumSelected"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import { useToastsStore } from "@stores/toasts";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpSegmentedControl from "@components/ui/SrpSegmentedControl.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import PickFromExistingContentModal from "@views/Community/ContentCalendar/steps/modals/PickFromExistingContentModal.vue";
import ContentSummary from "@views/Community/ContentCalendar/eventSnippets/ContentSummary.vue";
import ViewAllPhotosModal from "@views/Community/ContentCalendar/steps/modals/ViewAllPhotosModal.vue";
import PostMediaSelectionPanel from "@components/SocialPostEditAndShareModal/PostMediaSelectionPanel.vue";
import PostCaptionEditorPanel from "@components/SocialPostEditAndShareModal/PostCaptionEditorPanel.vue";

// Types
import { ContentCalendarItem, ContentCalendarItemMatch, ContentSummaryPreview, SocialPostType } from "@contracts/contentCalendar";
import type { IconName } from "@contracts/IconName";
import { SocialShareSummaryPayload, SocialShareSummaryContentType } from "@contracts/shareStop";

// Repos
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";

// Constants
import { getGlobalRemoteLogger } from "@/helpers/RemoteLogger";
import FileUtils from "@logic/FileUtils";
import { filterAlbumAssetsForPostType, isCaptionRequiredForPostType, validateManualPostInputs, validateManualPostMediaMetadata } from "@logic/AtlasSchedulingValidation";
import { usePostTypeMediaRules } from "@composables/usePostTypeMediaRules";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    customerId: string;
    weekGroup?: string | null;
    /** When opening create flow, start in this mode ("ai" | "manual"). */
    initialMode?: "ai" | "manual" | null;
    initialTitle?: string | null;
    initialDescription?: string | null;
    /** Prefill manual mode caption when opening create flow. */
    initialCaption?: string | null;
  }>(),
  {
    weekGroup: null,
    initialMode: null,
    initialTitle: null,
    initialDescription: null,
    initialCaption: null,
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "createCalendarItem", item: ContentCalendarItem): void;
  (e: "createNewItemAndPost", newItemAndPost: ContentCalendarItemMatch): void;
  (e: "cancel"): void;
}>();

// Local values ===============================================================
const _title = ref<string>("");
const _description = ref<string>("");

// Mode toggle ================================================================
const mode = ref<string>("ai");
const postType = ref<SocialPostType>(SocialPostType.Post);
const postTypeOptions: SocialPostType[] = [SocialPostType.Post, SocialPostType.Reel, SocialPostType.Story];
const postTypeIconMap: Record<SocialPostType, IconName> = {
  [SocialPostType.Post]: "social-post",
  [SocialPostType.Reel]: "social-reel",
  [SocialPostType.Story]: "social-story",
};
const modeOptions = computed(() => [
  { value: "ai", label: "AI Assisted", icon: "ai_2" as const, disabled: postType.value !== SocialPostType.Post },
  { value: "manual", label: "Manual Mode", icon: "edit-pencil_2" as const },
]);
const postTypeSegmentOptions = computed(() =>
  postTypeOptions.map(type => ({
    value: type,
    label: type,
    icon: postTypeIconMap[type],
  }))
);
const showAiPostOnlyWarning = computed(() => postType.value === SocialPostType.Reel || postType.value === SocialPostType.Story);

const isStoryPost = computed(() => postType.value === SocialPostType.Story);
const { mediaSectionLabel, videoOnlyForAlbumPicker, showAlbumPhotoAndVideoCount } = usePostTypeMediaRules(postType);

const isCaptionRequired = computed(() => isCaptionRequiredForPostType(postType.value));

const manualValidationError = ref<string>("");
const manualValidationErrorMessage = computed(() => {
  if (isFormValid.value || mode.value !== "manual") {
    return "";
  }
  if (manualValidationError.value) {
    return manualValidationError.value;
  }
  const result = validateManualPostInputs({
    postType: postType.value,
    assetIds: manualSelectedAssetIds.value,
    caption: _caption.value,
  });
  return result.ok ? "" : result.message;
});

watch(postType, newType => {
  if (newType !== SocialPostType.Post && mode.value === "ai") {
    mode.value = "manual";
  }
  if (newType === SocialPostType.Reel) {
    manualSelectedAssetIds.value = manualSelectedAssetIds.value.filter(assetId => FileUtils.isVideoFileType(assetId)).slice(0, 1);
  } else if (newType === SocialPostType.Post) {
    manualSelectedAssetIds.value = manualSelectedAssetIds.value.filter(assetId => !FileUtils.isVideoFileType(assetId));
  } else if (newType === SocialPostType.Story) {
    manualSelectedAssetIds.value = manualSelectedAssetIds.value.slice(0, 1);
  }
  if (manualContentSummaryPreview.value) {
    loadManualAlbumPayload(manualContentSummaryPreview.value);
  }
  manualValidationError.value = "";
  isFormValid.value = true;
});

watch(mode, newMode => {
  if (newMode === "ai" && postType.value !== SocialPostType.Post) {
    mode.value = "manual";
  }
});

// Content group selection ====================================================
const selectedContentSummary = ref<ContentSummaryPreview | null>(null);
const isPickContentModalVisible = ref<boolean>(false);

function handleContentSummarySelected(contentSummary: ContentSummaryPreview) {
  selectedContentSummary.value = contentSummary;
  isPickContentModalVisible.value = false;
}

// View all photos modal ======================================================
const isViewAllPhotosModalVisible = ref<boolean>(false);
const viewAllPhotosSummaryId = ref<string>("");

function handleViewPhotos() {
  viewAllPhotosSummaryId.value = selectedContentSummary.value?.id || "";
  isViewAllPhotosModalVisible.value = true;
}

// Manual mode state ===========================================================
const _caption = ref<string>("");
const manualSelectedAssetIds = ref<string[]>([]);
const uploadedAssetIds = ref<string[]>([]);
const isManualAlbumPickerOpen = ref<boolean>(false);
const manualAlbumPayload = ref<SocialShareSummaryPayload | null>(null);
const manualContentSummaryPreview = ref<ContentSummaryPreview | null>(null);
const manualUploadsSelectedCount = ref<number>(0);
const isLoadingManualAlbum = ref<boolean>(false);

/** Declared before resetCreateFormState / immediate watch so setup does not hit the TDZ. */
const isFormValid = ref<boolean>(true);

function resetCreateFormState() {
  postType.value = SocialPostType.Post;
  mode.value = props.initialMode ?? "ai";
  _title.value = props.initialTitle ?? "";
  _description.value = props.initialDescription ?? "";
  _caption.value = props.initialCaption ?? "";
  manualSelectedAssetIds.value = [];
  uploadedAssetIds.value = [];
  manualUploadsSelectedCount.value = 0;
  isManualAlbumPickerOpen.value = false;
  manualAlbumPayload.value = null;
  manualContentSummaryPreview.value = null;
  selectedContentSummary.value = null;
  isPickContentModalVisible.value = false;
  manualValidationError.value = "";
  isFormValid.value = true;
}

function applyFormStateForOpenModal() {
  if (!props.isVisible) {
    return;
  }
  resetCreateFormState();
}

watch(
  () => [props.isVisible, props.initialMode, props.initialTitle, props.initialDescription, props.initialCaption] as const,
  () => {
    applyFormStateForOpenModal();
  },
  { immediate: true }
);

// Button text =================================================================
const buttonText = computed<string>(() => {
  if (mode.value === "manual") {
    return isSaving.value ? "Saving" : "Save";
  }
  return isSaving.value ? "Generating" : "Generate";
});

// Manual mode helpers ========================================================
function handleMediaSelectionChanged() {
  manualValidationError.value = "";
  isFormValid.value = true;
}

function handlePhotoUploadSelect(_assetId: string) {
  handleMediaSelectionChanged();
  manualUploadsSelectedCount.value++;
}

function handlePhotoUploadDeselect(_assetId: string) {
  manualUploadsSelectedCount.value = Math.max(0, manualUploadsSelectedCount.value - 1);
}

async function handleManualAlbumSelected(contentSummary: ContentSummaryPreview) {
  manualContentSummaryPreview.value = contentSummary; // Store the preview for later
  isManualAlbumPickerOpen.value = false;
  await loadManualAlbumPayload(contentSummary);
}

async function loadManualAlbumPayload(contentSummary: ContentSummaryPreview) {
  isLoadingManualAlbum.value = true;
  try {
    manualAlbumPayload.value = await ShareStopSummaryRepo.loadSocialShareSummaryPayloadFromSummaryId(props.customerId, contentSummary.id, SocialShareSummaryContentType.All);
  } finally {
    isLoadingManualAlbum.value = false;
  }
}

const displayedManualAlbumAssetIds = computed(() => {
  if (!manualAlbumPayload.value) {
    return [];
  }
  return filterAlbumAssetsForPostType(manualAlbumPayload.value.assets, postType.value);
});

// Save =======================================================================
const isSaving = ref<boolean>(false);

async function save(): Promise<boolean> {
  isSaving.value = true;

  if (mode.value === "manual") {
    manualValidationError.value = "";
    const syncResult = validateManualPostInputs({
      postType: postType.value,
      assetIds: manualSelectedAssetIds.value,
      caption: _caption.value,
    });
    if (!syncResult.ok) {
      isFormValid.value = false;
      manualValidationError.value = syncResult.message;
      isSaving.value = false;
      return false;
    }

    const metadataResult = await validateManualPostMediaMetadata({
      postType: postType.value,
      assetIds: manualSelectedAssetIds.value,
    });
    if (!metadataResult.ok) {
      isFormValid.value = false;
      manualValidationError.value = metadataResult.message;
      isSaving.value = false;
      return false;
    }

    const response = await ContentCalendarRepo.createManualPost(props.customerId, {
      caption: isStoryPost.value ? "" : _caption.value,
      assetIds: manualSelectedAssetIds.value,
      postType: postType.value,
      weekGroup: props.weekGroup,
      contentSourceType: manualContentSummaryPreview.value?.contextType,
      sourceEntityId: manualContentSummaryPreview.value?.contextId,
      manualUploadsSelected: manualUploadsSelectedCount.value,
    });

    // We have both the post and item created immediately
    emit("createNewItemAndPost", response.newItemAndPost);
    isSaving.value = false;
    return true;
  } else if (mode.value === "ai") {
    if (postType.value !== SocialPostType.Post) {
      isFormValid.value = false;
      isSaving.value = false;
      return false;
    }

    // AI mode validation and save
    if (!_title.value || !_description.value) {
      isFormValid.value = false;
      isSaving.value = false;
      return false;
    }

    const response = await ContentCalendarRepo.matchAndGeneratePost(props.customerId, {
      title: _title.value,
      description: _description.value,
      weekGroup: props.weekGroup,
      contentSourceType: selectedContentSummary.value?.contextType,
      sourceEntityId: selectedContentSummary.value?.contextId,
    });

    if (response.failedToMatch) {
      useToastsStore().warn({
        message: "Couldn't find suitable content",
        expiresInMs: 8000,
        details: "The guidance was created but we couldn't match it to any of your existing content.",
      });
    }

    // We just have the item created and the post will generate async
    emit("createCalendarItem", response.calendarItem);
    isSaving.value = false;
    return true;
  }
  getGlobalRemoteLogger().error(`NewPostModal: Unknown mode ${mode.value} for ${props.customerId}`);
  isSaving.value = false;
  return false;
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

.input-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 3px !important;
}

.create-type-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.create-type-row__label {
  font-size: 1.5rem;
  margin-bottom: 0;
  white-space: nowrap;
}

.create-type-row__ai-warning {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin: 8px 0 0;
}

// Photo section container ======================================================
.photo-section-container {
  padding: 12px;
  border-radius: 4px;
  transition:
    border-color 0.05s ease-in-out,
    box-shadow 0.05s ease-in-out;

  &--error {
    border: 1px rgba(178, 34, 34, 0.6) solid;
    box-shadow: 0 0 0 5px rgba(178, 34, 34, 0.2);
  }
}
</style>
